/**
 * EpochVariant - Variante Unix Epoch para Date
 *
 * Representa data como segundos desde epoch (Unix timestamp).
 *
 * @example
 * 1733425800 // 2024-12-05T19:30:00Z
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { DateEpoch, DateTimestamp, DateFormatOptions } from "../types";

export class EpochVariant extends BaseVariant<DateEpoch, DateTimestamp> {
  readonly name = "epoch";
  readonly type = "number" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: DateEpoch): DateTimestamp {
    // Epoch (segundos) → timestamp (milissegundos)
    return value * 1000;
  }

  fromBase(base: DateTimestamp): DateEpoch {
    // timestamp (milissegundos) → epoch (segundos)
    return Math.floor(base / 1000);
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): DateEpoch | null {
    // Já é número
    if (typeof input === "number") {
      if (!Number.isFinite(input)) return null;
      // Se parece ser milissegundos (> 1e10), converte para segundos
      if (Math.abs(input) > 1e11) {
        return Math.floor(input / 1000);
      }
      return Math.floor(input);
    }

    // String numérica
    if (typeof input === "string") {
      const trimmed = input.trim();

      // String numérica pura
      if (/^-?\d+$/.test(trimmed)) {
        const num = parseInt(trimmed, 10);
        if (Number.isFinite(num)) {
          // Se parece ser milissegundos (> 1e10), converte para segundos
          if (Math.abs(num) > 1e11) {
            return Math.floor(num / 1000);
          }
          return num;
        }
      }

      // Tenta parsear como ISO date e converter
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        return Math.floor(date.getTime() / 1000);
      }

      return null;
    }

    // Date object
    if (input instanceof Date) {
      const ts = input.getTime();
      return isNaN(ts) ? null : Math.floor(ts / 1000);
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: DateEpoch): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "number") {
      errors.push("Epoch must be a number");
      return { valid: false, errors };
    }

    if (!Number.isFinite(value)) {
      errors.push("Epoch must be a finite number");
      return { valid: false, errors };
    }

    if (!Number.isInteger(value)) {
      errors.push("Epoch must be an integer");
      return { valid: false, errors };
    }

    return { valid: true, errors: [] };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: DateEpoch, options?: FormatOptions): string {
    const opts = options as DateFormatOptions | undefined;
    const date = new Date(value * 1000);

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    // Se não especificar opções, retorna o epoch como string
    if (
      !opts ||
      (!opts.dateStyle && !opts.timeStyle && !opts.dateOnly && !opts.timeOnly)
    ) {
      return String(value);
    }

    const locale = opts.locale || "en-US";
    const formatOptions: Intl.DateTimeFormatOptions = {};

    if (opts.timeZone) {
      formatOptions.timeZone = opts.timeZone;
    }

    if (opts.dateOnly) {
      formatOptions.dateStyle = opts.dateStyle || "medium";
    } else if (opts.timeOnly) {
      formatOptions.timeStyle = opts.timeStyle || "medium";
    } else {
      if (opts.dateStyle) formatOptions.dateStyle = opts.dateStyle;
      if (opts.timeStyle) formatOptions.timeStyle = opts.timeStyle;
    }

    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  }
}
